import { permissionList } from './permission.model';

/**
 * How permissions depend on each other, for the role editor and the user
 * detail page. Purely informational: nothing here grants or enforces anything.
 */

/**
 * Group-level info shown via the ℹ icon on each permission group header.
 * Describes how the group's permissions relate to other groups.
 */
export interface PermissionGroupDependencyInfo {
  /** Short description of what this group controls */
  description: string;
  /** Permission values from OTHER groups that enhance this group's features */
  relatedPermissions: string[];
}

/**
 * How much a permission misses another one.
 *
 * `required`: it does not do what its name says without it — the page it
 * opens cannot be reached or cannot load, or the action it allows cannot be
 * completed. `optional`: it works, but part of its pages stays hidden until
 * the other one is granted too.
 */
export type DependencyStrength = 'required' | 'optional';

/** One reason a permission needs others. */
export interface PermissionDependency {
  /** The permissions it needs; a notice names only those the role lacks. */
  permissions: string[];
  strength: DependencyStrength;
  /**
   * What they are for, completing the sentence the notice opens with:
   * "Zonder <ontbrekende rechten> …" when required, "Met <ontbrekende
   * rechten> erbij …" when optional. Dutch, without the closing period.
   */
  reason: string;
}

/** A dependency left unmet, as the role editor and the user page show it. */
export interface DependencyNotice {
  /** The granted permission that needs the missing ones */
  permission: string;
  /** What it needs that the role does not have */
  missing: string[];
  strength: DependencyStrength;
  /** The sentence to show (Dutch) */
  message: string;
}

/** Group-level dependency info keyed by group key (matches PERMISSION_GROUPS keys) */
export const PERMISSION_GROUP_INFO: Record<string, PermissionGroupDependencyInfo> = {
  client: {
    description:
      'Cliëntrechten geven toegang tot de cliënt workspace. ' +
      'Binnen de workspace worden ook rechten voor Interacties, Planning, ' +
      'Signalering en Apparaten gebruikt.',
    relatedPermissions: [
      'interaction:read',
      'client_event:read',
      'scm:read',
      'device:read',
    ],
  },
  clientEvent: {
    description:
      'Planningrechten worden gebruikt binnen de cliënt workspace. ' +
      'Om interacties in te plannen is ook leestoegang tot Interacties nodig.',
    relatedPermissions: ['client:read', 'interaction:read'],
  },
  interaction: {
    description:
      'Interactierechten worden ook gebruikt in Planning en Signalering. ' +
      'De interactie-editor gebruikt Media voor audio-acties, Contacten ' +
      'voor SMS-notificaties en Tags en Variabelen in de zijbalk.',
    relatedPermissions: ['media:read', 'contact:read', 'tag:read', 'var:read'],
  },
  scm: {
    description:
      'Signaleringsrechten worden gebruikt binnen de cliënt workspace. ' +
      'Leestoegang tot Interacties is nodig om gekoppelde interacties te bekijken. ' +
      '"Media lezen" is nodig om unit-media te importeren naar een cliënt.',
    relatedPermissions: ['client:read', 'interaction:read', 'media:read'],
  },
  media: {
    description:
      'Mediarechten worden ook gebruikt in de interactie-editor voor audio-acties. ' +
      'Combineer met Groepenrechten voor volledig folderbeheer.',
    relatedPermissions: ['group:create', 'group:read', 'group:update', 'group:delete'],
  },
  group: {
    description:
      'Groepenrechten worden gebruikt voor folderbeheer in de mediabibliotheek.',
    relatedPermissions: ['media:read', 'media:create'],
  },
  user: {
    description:
      'Gebruikersrechten beheren het gebruikersbeheer. ' +
      'Combineer met Rollenrechten om roltoewijzingen te kunnen zien en beheren.',
    relatedPermissions: ['user_role:read'],
  },
  userRole: {
    description:
      'Rollenrechten beheren de rol- en rechtentoewijzing. ' +
      'Wordt vaak gecombineerd met Gebruikersrechten.',
    relatedPermissions: ['user:read'],
  },
  device: {
    description:
      'Apparaatrechten geven toegang tot apparaten en robots. ' +
      'Apparaten zijn ook zichtbaar binnen de cliënt workspace.',
    relatedPermissions: ['client:read'],
  },
  contact: {
    description:
      'Contactrechten worden ook gebruikt in de interactie-editor ' +
      'voor SMS-notificatie acties.',
    relatedPermissions: ['interaction:read', 'interaction:update'],
  },
  unit: {
    description:
      'Unitrechten beheren de organisatiestructuur (units). ' +
      'De algemene instellingenpagina gebruikt deze rechten. ' +
      'Bij het verplaatsen van units zijn ook Mediarechten nodig.',
    relatedPermissions: ['media:create', 'media:read'],
  },
  tag: {
    description:
      'Tagrechten beheren de tags die gebruikt kunnen worden voor ' +
      'het categoriseren van cliënten en andere items. ' +
      '"Tags lezen" wordt ook buiten de tagpagina gebruikt: voor het ' +
      'tagfilter op de interactie- en templateoverzichten, het toevoegen ' +
      'van tags in de interactie-editor en de mappen in de signalering.',
    relatedPermissions: ['interaction:read', 'interaction:update', 'scm:update'],
  },
  auditLog: {
    description: 'Audit log rechten geven leestoegang tot het auditlogboek.',
    relatedPermissions: [],
  },
  notificationLog: {
    description:
      'Geeft leestoegang tot het notificatielogboek, met de verstuurde ' +
      'mail- en SMS-notificaties.',
    relatedPermissions: [],
  },
  download: {
    description: 'Downloadrechten geven toegang tot de downloadpagina.',
    relatedPermissions: [],
  },
  var: {
    description:
      'Variabelenrechten beheren de variabelen die gebruikt worden ' +
      'binnen interacties. "Variabelen lezen" wordt ook buiten de ' +
      'variabelenpagina gebruikt: in de interactie-editor en de ' +
      'cliëntinstellingen.',
    relatedPermissions: ['interaction:read', 'client:read'],
  },
  supportSession: {
    description:
      'Supporttoegangsrechten bepalen wie supportcodes en -sessies kan inzien ' +
      'en aanmaken. Bij een supportcode worden de rollen van de ' +
      'supportmedewerker gekozen, daarvoor is "Rollen lezen" nodig.',
    relatedPermissions: ['user_role:read'],
  },
  aiAgentSettings: {
    description:
      'AI-agent instellingen beheren de selectie van taalmodellen en weergave van systeemprompts voor de AI-assistenten.',
    relatedPermissions: [],
  },
};

/**
 * What each permission needs from other groups, keyed by permission value.
 *
 * A permission's own group's `:read` is left out: the role editor checks that
 * one for every group. Keep this in step with the pages: a permission whose
 * page starts asking for another one gets an entry here, or a role can be
 * saved that looks complete and is not.
 */
export const PERMISSION_DEPENDENCIES: Record<string, PermissionDependency[]> = {
  // --- Client ---
  'client:read': [
    {
      permissions: ['interaction:read', 'client_event:read', 'scm:read', 'device:read'],
      strength: 'optional',
      reason: 'toont de cliënt workspace ook die onderdelen',
    },
    {
      permissions: ['var:read'],
      strength: 'optional',
      reason: 'tonen de cliëntinstellingen ook de variabelen',
    },
  ],
  'client:update': [
    {
      permissions: ['var:read', 'var:update'],
      strength: 'optional',
      reason: 'kan deze rol ook variabelen per cliënt overschrijven',
    },
  ],

  // --- Client Events (Planning) ---
  'client_event:read': [
    {
      permissions: ['client:read'],
      strength: 'required',
      reason: 'is de planning niet te bereiken: die staat in de cliënt workspace',
    },
  ],
  'client_event:create': [
    {
      permissions: ['interaction:read'],
      strength: 'optional',
      reason: 'kan deze rol ook interacties inplannen',
    },
    {
      permissions: ['media:read'],
      strength: 'optional',
      reason: 'kan deze rol ook media inplannen',
    },
  ],

  // --- Interaction ---
  'interaction:read': [
    {
      permissions: ['tag:read'],
      strength: 'optional',
      reason: 'kan deze rol ook op tags filteren in de interactie- en templateoverzichten',
    },
    {
      permissions: ['var:read'],
      strength: 'optional',
      reason: 'toont de interactie-editor ook de variabelen',
    },
  ],
  'interaction:update': [
    {
      permissions: ['tag:read'],
      strength: 'optional',
      reason: 'kan deze rol ook tags aan interacties toevoegen',
    },
    {
      permissions: ['media:read'],
      strength: 'optional',
      reason: 'kan deze rol ook audio- en afbeeldingsacties toevoegen en wijzigen',
    },
    {
      permissions: ['contact:read'],
      strength: 'optional',
      reason: 'kan deze rol ook SMS-notificatieacties instellen',
    },
  ],
  'interaction:publish': [
    {
      permissions: ['scm:read'],
      strength: 'optional',
      reason: 'waarschuwt de editor ook als een signaleringsmap door het publiceren leeg raakt',
    },
  ],
  'interaction:delete': [
    {
      permissions: ['scm:read'],
      strength: 'optional',
      reason: 'krijgt deze rol ook een waarschuwing als een signaleringsmap door het verwijderen leeg raakt',
    },
  ],
  'interaction:move': [
    {
      permissions: ['media:create', 'media:read', 'contact:read'],
      strength: 'required',
      reason:
        'kan deze rol geen templates naar een andere unit verplaatsen: de media ' +
        'en contacten gaan daarbij mee',
    },
  ],

  // --- SCM (Signaling) ---
  'scm:read': [
    {
      permissions: ['client:read'],
      strength: 'required',
      reason: 'is de signalering niet te bereiken: die staat in de cliënt workspace',
    },
    {
      permissions: ['interaction:read'],
      strength: 'optional',
      reason: 'kan deze rol ook de gekoppelde interacties openen',
    },
  ],
  'scm:update': [
    {
      permissions: ['media:read'],
      strength: 'optional',
      reason: 'kan deze rol ook audio en afbeeldingen aan signaleringsblokken koppelen',
    },
    {
      permissions: ['interaction:read'],
      strength: 'optional',
      reason: 'kan deze rol ook interacties aan signaleringsblokken toevoegen',
    },
    {
      permissions: ['tag:read'],
      strength: 'optional',
      reason: 'kan deze rol ook tags aan mappen in de signalering toevoegen',
    },
  ],

  // --- Media ---
  'media:read': [
    {
      permissions: ['media:share'],
      strength: 'optional',
      reason: 'toont de mediabibliotheek ook de gedeelde media',
    },
    {
      permissions: ['group:read'],
      strength: 'optional',
      reason: 'toont de mediabibliotheek ook de mappen',
    },
  ],
  'media:create': [
    {
      permissions: ['group:create'],
      strength: 'optional',
      reason: 'kan deze rol ook mappen aanmaken',
    },
    {
      permissions: ['group:read'],
      strength: 'optional',
      reason: 'kan deze rol ook bestanden naar een map verplaatsen',
    },
  ],
  'media:delete': [
    {
      permissions: ['group:delete'],
      strength: 'optional',
      reason: 'kan deze rol ook mappen verwijderen',
    },
  ],

  // --- Groups (folders) ---
  'group:read': [
    {
      permissions: ['media:read'],
      strength: 'required',
      reason: 'is de mediabibliotheek, waar de mappen staan, niet te openen',
    },
  ],
  'group:create': [
    {
      permissions: ['media:read'],
      strength: 'required',
      reason: 'is de mediabibliotheek, waar de mappen staan, niet te openen',
    },
  ],
  'group:update': [
    {
      permissions: ['media:read'],
      strength: 'required',
      reason: 'is de mediabibliotheek, waar de mappen staan, niet te openen',
    },
  ],
  'group:delete': [
    {
      permissions: ['media:read'],
      strength: 'required',
      reason: 'is de mediabibliotheek, waar de mappen staan, niet te openen',
    },
    {
      permissions: ['media:delete'],
      strength: 'required',
      reason: 'kan deze rol geen mappen verwijderen: de media erin worden mee verwijderd',
    },
  ],

  // --- User ---
  'user:read': [
    {
      permissions: ['user_role:read'],
      strength: 'optional',
      reason: 'ziet deze rol ook welke rol een gebruiker heeft en wat die rol mag',
    },
  ],
  'user:create': [
    {
      permissions: ['user_role:read'],
      strength: 'required',
      reason: 'kan deze rol geen gebruikers aanmaken: daarbij is een rol kiezen verplicht',
    },
  ],
  'user:update': [
    {
      permissions: ['user_role:read'],
      strength: 'optional',
      reason: 'kan deze rol ook de rol van een gebruiker wijzigen',
    },
  ],

  // --- Unit ---
  'unit:move': [
    {
      permissions: ['media:create', 'media:read'],
      strength: 'required',
      reason:
        'kan deze rol geen units verplaatsen: gedeelde media worden daarbij naar ' +
        'de mediabibliotheek van de unit gekopieerd',
    },
  ],

  // --- Support session ---
  'support_session:create': [
    {
      permissions: ['user_role:read'],
      strength: 'required',
      reason:
        'kan deze rol geen supportcodes aanmaken: daarbij worden de rollen van de ' +
        'supportmedewerker gekozen',
    },
  ],

  // --- Var ---
  'var:update': [
    {
      permissions: ['client:read', 'client:update'],
      strength: 'optional',
      reason: 'kan deze rol variabelen ook per cliënt overschrijven',
    },
  ],

};

/**
 * Permissions that work without their own group's `:read`, which the role
 * editor otherwise asks for whenever anything else in a group is ticked: the
 * page they need is guarded by the permission itself.
 */
export const WORKS_WITHOUT_GROUP_READ: ReadonlySet<string> = new Set(['unit:move']);

/**
 * The dependencies `granted` leaves unmet, required ones first, each with the
 * sentence to show for it.
 */
export function unmetDependencies(granted: ReadonlySet<string>): DependencyNotice[] {
  const notices: DependencyNotice[] = [];
  const messages = new Set<string>();
  for (const [permission, dependencies] of Object.entries(PERMISSION_DEPENDENCIES)) {
    if (!granted.has(permission)) continue;
    for (const dependency of dependencies) {
      const missing = dependency.permissions.filter((p) => !granted.has(p));
      if (missing.length === 0) continue;
      // Permissions with the same need share its sentence (every folder
      // permission needs the media library): say it once.
      const message = dependencyMessage(dependency, missing);
      if (messages.has(message)) continue;
      messages.add(message);
      notices.push({ permission, missing, strength: dependency.strength, message });
    }
  }
  return notices.sort(
    (a, b) => Number(a.strength === 'optional') - Number(b.strength === 'optional')
  );
}

/** The notice sentence for `dependency`, naming only the `missing` permissions. */
function dependencyMessage(dependency: PermissionDependency, missing: string[]): string {
  const names = permissionList(missing);
  return dependency.strength === 'required'
    ? `Zonder ${names} ${dependency.reason}.`
    : `Met ${names} erbij ${dependency.reason}.`;
}
